import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { BiEdit } from "react-icons/bi";
import { IoTrashOutline } from "react-icons/io5";
import PageHeader from "../../component/pageHeaders";
import { API } from "../../config/API";
import { DELETE, GET, PATCH, POST, PUT } from "../../utils/apirequest";
import { CountryDto } from "../../config/countries";
import { ZoneDto } from "../../config/zones";
import { loadEpaperConfig } from "../../config/epaperConfig";
import { apiErrorMessage } from "./utils";
import "./styles.css";

type FormValues = {
  country_id: number;
  name: string;
  code: string;
  is_active: boolean;
  sort_order: number;
};

function EpaperZones() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const countryFilter = params.get("country") || "";
  const [form] = Form.useForm<FormValues>();
  const [zones, setZones] = useState<ZoneDto[]>([]);
  const [countries, setCountries] = useState<CountryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<ZoneDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const [z, c]: any[] = await Promise.all([
        GET(API.EPAPER_SETTINGS_ZONES, null, null),
        GET(API.EPAPER_SETTINGS_COUNTRIES, null, null),
      ]);
      setZones(Array.isArray(z?.data) ? z.data : []);
      setCountries(Array.isArray(c?.data) ? c.data : []);
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not load zones."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const afterChange = async () => {
    await Promise.all([load(), loadEpaperConfig(true)]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return zones.filter(
      (z) =>
        (!countryFilter || z.country_code === countryFilter) &&
        (!q ||
          [z.name, z.code, z.country_name].some((v) =>
            String(v || "").toLowerCase().includes(q)
          ))
    );
  }, [zones, search, countryFilter]);

  // Back to the first page when the search or country filter changes;
  // stay in range after a delete.
  useEffect(() => {
    setPage(1);
  }, [search, countryFilter]);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const countryOptions = (activeOnly: boolean, keepId?: number | null) =>
    countries
      .filter((c) => !activeOnly || c.is_active || c.id === keepId)
      .map((c) => ({
        value: c.id,
        label: `${c.name}${c.is_active ? "" : " (inactive)"}`,
      }));

  const setCountryFilter = (code?: string) => {
    const next = new URLSearchParams(params);
    if (code) next.set("country", code);
    else next.delete("country");
    setParams(next);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    const preselect = countries.find((c) => c.code === countryFilter);
    const inCountry = zones.filter((z) => !preselect || z.country_id === preselect.id);
    form.setFieldsValue({
      country_id: preselect?.id,
      is_active: true,
      sort_order: inCountry.length + 1,
    } as any);
    setModalOpen(true);
  };

  const openEdit = (row: ZoneDto) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      country_id: row.country_id ?? undefined,
      name: row.name,
      code: row.code,
      is_active: row.is_active,
      sort_order: row.sort_order,
    });
    setModalOpen(true);
  };

  const onSave = async (values: FormValues) => {
    setSaving(true);
    const payload = {
      data: { ...values, code: String(values.code || "").trim().toUpperCase() },
    };
    try {
      if (editing) {
        await PUT(`${API.EPAPER_SETTINGS_ZONES}/${editing.id}`, payload);
        message.success("Zone updated");
      } else {
        await POST(API.EPAPER_SETTINGS_ZONES, payload);
        message.success("Zone added");
      }
      setModalOpen(false);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not save zone."));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row: ZoneDto, active: boolean) => {
    setTogglingId(row.id);
    try {
      await PATCH(`${API.EPAPER_SETTINGS_ZONES}/${row.id}/status`, {
        data: { is_active: active },
      });
      message.success(`${row.name} ${active ? "enabled" : "disabled"}`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not update status."));
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (row: ZoneDto) => {
    try {
      await DELETE(`${API.EPAPER_SETTINGS_ZONES}/${row.id}`);
      message.success(`${row.name} deleted`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not delete zone."));
    }
  };

  const columns: ColumnsType<ZoneDto> = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_t, _r, i) => (currentPage - 1) * pageSize + i + 1,
    },
    { title: "Zone Name", dataIndex: "name", key: "name" },
    { title: "Zone Code", dataIndex: "code", key: "code", width: 120 },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      render: (name: string) => name || "—",
    },
    { title: "Display Order", dataIndex: "sort_order", key: "sort_order", width: 120 },
    {
      title: "Status",
      key: "is_active",
      width: 140,
      render: (_t, row) => (
        <Space>
          <Switch
            size="small"
            checked={row.is_active}
            loading={togglingId === row.id}
            onChange={(checked) => toggleStatus(row, checked)}
          />
          <span>{row.is_active ? "Active" : "Inactive"}</span>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_t, row) => (
        <Space size="middle">
          <BiEdit size={20} color="#f5a442" cursor="pointer" onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete Zone"
            description={`Delete ${row.name}? Zones in use can only be disabled.`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => remove(row)}
          >
            <IoTrashOutline size={20} color="red" cursor="pointer" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={"Zones"}
        loader={loading}
        length={"Dashboard / E-Paper Settings / Zones"}
        onBack={() => navigate("/Auth/epaper-settings")}
      >
        <div className="homescreen-pageItem">
          <Select
            style={{ width: 200 }}
            value={countryFilter || ""}
            onChange={(v) => setCountryFilter(v || undefined)}
            options={[
              { value: "", label: "All Countries" },
              ...countries.map((c) => ({
                value: c.code,
                label: c.name,
              })),
            ]}
          />
          &nbsp;
          <Input.Search
            style={{ width: 220 }}
            allowClear
            placeholder="Search zone..."
            onChange={(e) => setSearch(e.target.value)}
          />
          &nbsp;
          <Button type="primary" onClick={openCreate}>
            + Add Zone
          </Button>
        </div>
      </PageHeader>
      <div className="epaperSettings-subtitle" style={{ paddingTop: 10 }}>
        Manage zones and assign them to countries. Countries without zones use
        the country itself as the zone.
      </div>
      <Table
        columns={columns}
        dataSource={pageRows}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
      />
      <br />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Pagination
          responsive
          current={currentPage}
          total={filtered.length}
          pageSize={pageSize}
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </div>
      <br />

      <Modal
        title={editing ? "Edit Zone" : "Add Zone"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave} requiredMark>
          <Form.Item
            label="Country"
            name="country_id"
            rules={[{ required: true, message: "Please select a country" }]}
          >
            <Select
              placeholder="Select country"
              showSearch
              optionFilterProp="label"
              options={countryOptions(true, editing?.country_id)}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Zone Name"
                name="name"
                rules={[{ required: true, whitespace: true, message: "Zone name is required" }]}
              >
                <Input placeholder="Abu Dhabi" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Zone Code"
                name="code"
                extra={
                  editing
                    ? "Cannot be changed (used in receipt numbers)"
                    : "2–5 letters/digits, unique. Appears in receipt numbers"
                }
                rules={[
                  { required: true, message: "Zone code is required" },
                  { pattern: /^[A-Za-z0-9]{2,5}$/, message: "2–5 letters or digits" },
                ]}
              >
                <Input placeholder="AUH" maxLength={5} disabled={!!editing} style={{ textTransform: "uppercase" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Display Order"
                name="sort_order"
                rules={[{ type: "integer", min: 0, message: "Whole number ≥ 0" }]}
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="is_active" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={true}>Active</Radio>
                  <Radio value={false}>Inactive</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default EpaperZones;
